
import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Image as ImageIcon,
  X,
  ChevronLeft,
  ChevronRight,
  ArrowUpRight,
  Camera,
  Sparkles,
  AlertCircle,
} from "lucide-react";
import axios from "axios";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
const IMAGE_SOURCE_URL = import.meta.env.VITE_API_SOURCE_URL;

const fadeUp = {
  hidden: { opacity: 0, y: 35 },
  visible: { opacity: 1, y: 0 },
};

export default function Gallery({
  accent = "#115e32",
  pageTitle = "Gallery | University of Creative Technology Chittagong",
}) {
  const [images, setImages] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    document.title = pageTitle;
  }, [pageTitle]);

  useEffect(() => {
    const fetchGallery = async () => {
      setLoading(true);
      setError(null);

      try {
        const payload = {
          userId: import.meta.env.VITE_API_USER,
          password: import.meta.env.VITE_API_PASS,
          department_id: "0",
        };

        const api = axios.create({
          baseURL: API_BASE_URL,
          headers: {
            "Content-Type": "application/json",
          },
        });

        const response = await api.post("/gallery", payload);
        const data = Array.isArray(response.data) ? response.data : [];

        setImages(data);
      } catch (err) {
        setError(
          err.response?.data?.message ||
            err.message ||
            "Unable to load the gallery."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchGallery();
  }, []);

  const openLightbox = useCallback((index) => {
    setCurrentIndex(index);
  }, []);

  const closeLightbox = useCallback(() => {
    setCurrentIndex(null);
  }, []);

  const prevImage = useCallback(() => {
    setCurrentIndex((prev) =>
      prev === null ? null : (prev - 1 + images.length) % images.length
    );
  }, [images.length]);

  const nextImage = useCallback(() => {
    setCurrentIndex((prev) =>
      prev === null ? null : (prev + 1) % images.length
    );
  }, [images.length]);

  useEffect(() => {
    if (currentIndex === null) return;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") closeLightbox();
      if (event.key === "ArrowLeft") prevImage();
      if (event.key === "ArrowRight") nextImage();
    };

    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [currentIndex, closeLightbox, prevImage, nextImage]);

  const getImageUrl = (picture) => {
    if (!picture) return "";
    if (picture.startsWith("http")) return picture;
    return `${IMAGE_SOURCE_URL}${picture}`;
  };

  return (
    <main className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <section className="relative isolate overflow-hidden bg-gradient-to-br from-[#014864] via-[#075c78] to-[#6BCCF2] text-white">
        {/* Background decorations */}
        <div className="absolute inset-0 -z-10 overflow-hidden">
          <motion.div
            animate={{ x: [0, 35, 0], y: [0, -25, 0] }}
            transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
            className="absolute -right-24 -top-28 h-96 w-96 rounded-full bg-cyan-300/20 blur-3xl"
          />
          <motion.div
            animate={{ x: [0, -25, 0], y: [0, 20, 0] }}
            transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
            className="absolute -bottom-40 -left-20 h-96 w-96 rounded-full bg-white/10 blur-3xl"
          />
          <div className="absolute inset-0 opacity-[0.08] [background-image:radial-gradient(#fff_1px,transparent_1px)] [background-size:24px_24px]" />
        </div>

        <div className="mx-auto max-w-7xl px-6 pb-28 pt-20 md:px-10 md:pb-36 md:pt-28">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={{
              hidden: {},
              visible: {
                transition: {
                  staggerChildren: 0.15,
                },
              },
            }}
            className="mx-auto max-w-3xl text-center"
          >
            <motion.div
              variants={fadeUp}
              transition={{ duration: 0.7 }}
              className="mb-7 inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-5 py-2 text-sm font-semibold backdrop-blur-md"
            >
              <Sparkles className="h-4 w-4 text-cyan-200" />
              Moments That Matter
            </motion.div>

            <motion.div
              variants={fadeUp}
              transition={{ duration: 0.7 }}
              className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-3xl border border-white/20 bg-white/10 shadow-2xl backdrop-blur-xl"
            >
              <Camera className="h-10 w-10 text-white" />
            </motion.div>

            <motion.h1
              variants={fadeUp}
              transition={{ duration: 0.7 }}
              className="text-4xl font-extrabold tracking-tight sm:text-5xl md:text-7xl"
            >
              Our Image{" "}
              <span className="text-cyan-200">Gallery</span>
            </motion.h1>

            <motion.p
              variants={fadeUp}
              transition={{ duration: 0.7 }}
              className="mx-auto mt-6 max-w-2xl text-base leading-8 text-white/80 md:text-lg"
            >
              Explore the vibrant campus life, memorable events, achievements,
              and unforgettable moments of the University of Creative
              Technology Chittagong.
            </motion.p>

            <motion.div
              variants={fadeUp}
              transition={{ duration: 0.7 }}
              className="mt-9 flex justify-center"
            >
              <div className="flex items-center gap-3 rounded-2xl border border-white/20 bg-white/10 px-5 py-3 backdrop-blur-md">
                <ImageIcon className="h-5 w-5 text-cyan-200" />
                <span className="text-sm font-medium">
                  {loading
                    ? "Discovering memories..."
                    : `${images.length} memorable moments`}
                </span>
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* Wave */}
        <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none">
          <svg
            viewBox="0 0 1440 120"
            className="block h-16 w-full md:h-24"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <path
              d="M0,64 C240,120 480,0 720,55 C960,110 1200,20 1440,70 L1440,120 L0,120 Z"
              fill="#f8fafc"
            />
          </svg>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="relative mx-auto max-w-7xl px-5 pb-20 pt-8 md:px-10 md:pb-28">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-10 flex flex-col justify-between gap-4 sm:flex-row sm:items-end"
        >
          <div>
            <div className="mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-[0.2em] text-[#115e32]">
              <span className="h-1 w-8 rounded-full bg-[#6BCCF2]" />
              Campus Memories
            </div>
            <h2 className="text-3xl font-extrabold tracking-tight text-[#014864] md:text-4xl">
              Moments in Focus
            </h2>
            <p className="mt-3 max-w-xl text-slate-500">
              A glimpse into the experiences, people, and moments that make
              our university special.
            </p>
          </div>

          {!loading && !error && images.length > 0 && (
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-500">
              <ImageIcon className="h-4 w-4 text-[#115e32]" />
              {images.length} Photos
            </div>
          )}
        </motion.div>

        {/* Loading Skeleton */}
        {loading && (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {Array.from({ length: 8 }).map((_, index) => (
              <div
                key={index}
                className="overflow-hidden rounded-2xl border border-slate-200 bg-white p-2 shadow-sm"
              >
                <div className="h-64 animate-pulse rounded-xl bg-slate-200" />
                <div className="mx-2 my-4 h-3 w-2/3 animate-pulse rounded bg-slate-200" />
              </div>
            ))}
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <div className="mx-auto max-w-xl rounded-3xl border border-red-100 bg-white p-8 text-center shadow-sm">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-red-50 text-red-500">
              <AlertCircle className="h-7 w-7" />
            </div>
            <h3 className="text-xl font-bold text-slate-800">
              Gallery could not be loaded
            </h3>
            <p className="mt-2 text-sm text-slate-500">{error}</p>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && images.length === 0 && (
          <div className="rounded-3xl border border-dashed border-slate-300 bg-white px-6 py-16 text-center">
            <ImageIcon className="mx-auto h-12 w-12 text-slate-300" />
            <h3 className="mt-4 text-xl font-bold text-slate-700">
              No photos available
            </h3>
            <p className="mt-2 text-slate-500">
              Gallery photos will appear here when they are available.
            </p>
          </div>
        )}

        {/* Gallery Cards */}
        {!loading && !error && images.length > 0 && (
          <motion.div
            initial="hidden"
            animate="visible"
            variants={{
              hidden: {},
              visible: {
                transition: {
                  staggerChildren: 0.07,
                },
              },
            }}
            className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
          >
            {images.map((img, index) => (
              <motion.button
                key={img.id || img.picture || index}
                type="button"
                variants={fadeUp}
                transition={{ duration: 0.55, ease: "easeOut" }}
                whileHover={{ y: -8 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => openLightbox(index)}
                className="group relative overflow-hidden rounded-2xl border border-slate-200/80 bg-white p-2 text-left shadow-sm transition-shadow duration-300 hover:shadow-2xl hover:shadow-[#014864]/15 focus:outline-none focus:ring-2 focus:ring-[#6BCCF2]"
              >
                <div className="relative h-64 overflow-hidden rounded-xl bg-slate-100">
                  <img
                    src={getImageUrl(img.picture)}
                    alt={img.title || "University gallery"}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
                  />

                  <div className="absolute inset-0 bg-gradient-to-t from-[#014864]/90 via-[#014864]/10 to-transparent opacity-70 transition-opacity duration-500 group-hover:opacity-100" />

                  <div className="absolute inset-0 flex items-center justify-center">
                    <motion.div
                      initial={{ opacity: 0, scale: 0.7 }}
                      whileHover={{ opacity: 1, scale: 1 }}
                      className="flex h-12 w-12 items-center justify-center rounded-full border border-white/40 bg-white/20 text-white backdrop-blur-md"
                    >
                      <ArrowUpRight className="h-6 w-6" />
                    </motion.div>
                  </div>

                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-cyan-200">
                      UCTC Gallery
                    </p>
                    <h3 className="line-clamp-2 text-base font-bold text-white">
                      {img.title || "Campus Memories"}
                    </h3>
                  </div>
                </div>
              </motion.button>
            ))}
          </motion.div>
        )}
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {currentIndex !== null && images[currentIndex] && (
          <motion.div
            key="gallery-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-[#001923]/95 p-4 backdrop-blur-md sm:p-8"
            onClick={closeLightbox}
          >
            {/* Close */}
            <button
              type="button"
              aria-label="Close image viewer"
              onClick={(event) => {
                event.stopPropagation();
                closeLightbox();
              }}
              className="absolute right-4 top-4 z-20 flex h-12 w-12 items-center justify-center rounded-full border border-white/15 bg-white/10 text-white transition hover:bg-white/20 sm:right-8 sm:top-8"
            >
              <X className="h-6 w-6" />
            </button>

            {/* Previous */}
            {images.length > 1 && (
              <button
                type="button"
                aria-label="Previous image"
                onClick={(event) => {
                  event.stopPropagation();
                  prevImage();
                }}
                className="absolute left-3 z-20 flex h-12 w-12 items-center justify-center rounded-full border border-white/15 bg-white/10 text-white transition hover:bg-[#6BCCF2] hover:text-[#014864] sm:left-8 sm:h-14 sm:w-14"
              >
                <ChevronLeft className="h-7 w-7" />
              </button>
            )}

            {/* Next */}
            {images.length > 1 && (
              <button
                type="button"
                aria-label="Next image"
                onClick={(event) => {
                  event.stopPropagation();
                  nextImage();
                }}
                className="absolute right-3 z-20 flex h-12 w-12 items-center justify-center rounded-full border border-white/15 bg-white/10 text-white transition hover:bg-[#6BCCF2] hover:text-[#014864] sm:right-8 sm:h-14 sm:w-14"
              >
                <ChevronRight className="h-7 w-7" />
              </button>
            )}

            {/* Image and caption */}
            <motion.div
              key={currentIndex}
              initial={{ opacity: 0, scale: 0.88, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.92, y: 10 }}
              transition={{ duration: 0.35, ease: "easeOut" }}
              onClick={(event) => event.stopPropagation()}
              className="flex max-h-full w-full max-w-6xl flex-col items-center"
            >
              <div className="relative flex min-h-0 w-full flex-1 items-center justify-center">
                <img
                  src={getImageUrl(images[currentIndex].picture)}
                  alt={images[currentIndex].title || "University gallery"}
                  className="max-h-[72vh] max-w-full rounded-xl object-contain shadow-2xl sm:max-h-[78vh]"
                />
              </div>

              <div className="mt-5 flex w-full max-w-3xl flex-col items-center gap-3 text-center sm:flex-row sm:justify-between sm:text-left">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-300">
                    University of Creative Technology Chittagong
                  </p>
                  <h3 className="mt-2 text-lg font-semibold text-white sm:text-xl">
                    {images[currentIndex].title || "Campus Memories"}
                  </h3>
                </div>

                <div className="rounded-full border border-white/15 bg-white/10 px-4 py-2 text-sm font-semibold text-white/80">
                  {currentIndex + 1} / {images.length}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}
